
import * as XLSX from 'xlsx';

// Service for handling spreadsheet operations
const N8N_WEBHOOK_URL = 'https://nestingale.app.n8n.cloud/webhook-test/2f3c2bfc-d4ba-438e-aa0c-a8844426d024';

export interface SampleData {
  firstRowItems: Record<string, any>;
}

export interface FileProcessingResponse {
  headers: string[];
  sampleData?: Record<string, any>;
}

// Function to trim query parameters from Google Sheet URL
export const trimGoogleSheetUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch (error) {
    console.error('Invalid URL:', error);
    return url;
  }
};

// Function to fetch sample data from Google Sheet
export const fetchSpreadsheetData = async (
  spreadsheetUrl: string,
  sheetName: string
): Promise<SampleData> => {
  console.log('Fetching data from n8n webhook');
  const trimmedUrl = trimGoogleSheetUrl(spreadsheetUrl);
  console.log('Spreadsheet URL:', trimmedUrl);
  console.log('Sheet Name:', sheetName);
  
  // Prepare payload for the n8n webhook
  const payload = {
    spreadsheetUrl: trimmedUrl,
    sheetName: sheetName
  };
  
  // Make actual API call to n8n webhook
  const response = await fetch(N8N_WEBHOOK_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload)
  });
  
  if (!response.ok) {
    throw new Error(`Server responded with status: ${response.status}`);
  }
  
  const data = await response.json();
  console.log('Webhook response:', data);
  
  if (data && data.firstRowItems) {
    return data;
  } else {
    throw new Error('Invalid data format received from webhook');
  }
};

// Function to process uploaded file and extract headers
export const processUploadedFile = async (file: File): Promise<FileProcessingResponse> => {
  console.log('Processing uploaded file:', file.name);
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const fileName = file.name.toLowerCase();
        
        if (fileName.endsWith('.csv')) {
          // Parse CSV headers
          const content = e.target?.result as string;
          const lines = content.split('\n');
          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
          
          console.log('CSV headers extracted:', headers);
          resolve({
            headers: headers.filter(h => h.length > 0),
            sampleData: {}
          });
        } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
          // Parse Excel file using SheetJS
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert to JSON to get headers
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length > 0) {
            const headers = (jsonData[0] as any[]).map(header => 
              header ? String(header).trim() : ''
            ).filter(h => h.length > 0);
            
            console.log('Excel headers extracted:', headers);
            resolve({
              headers,
              sampleData: {}
            });
          } else {
            reject(new Error('Excel file appears to be empty'));
          }
        } else {
          reject(new Error('Unsupported file format'));
        }
      } catch (error) {
        console.error('File parsing error:', error);
        reject(new Error('Failed to parse file: ' + (error instanceof Error ? error.message : 'Unknown error')));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    const fileName = file.name.toLowerCase();
    if (fileName.endsWith('.csv')) {
      reader.readAsText(file);
    } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
      reader.readAsArrayBuffer(file);
    } else {
      reject(new Error('Unsupported file format'));
    }
  });
};

// Function to submit mapping configuration
export const submitMappingConfiguration = async (mappings: any) => {
  console.log('Submitting mapping configuration:', mappings);
  
  // This would send the configuration to your backend
  // For now, we'll just simulate a successful response
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({ success: true, message: 'Mapping configuration submitted successfully' });
    }, 1000);
  });
};
